import { useEffect, useRef } from "react";

// ─────────────────────────────────────────────────────────────────────────
//  useAutofillSync — reconcile React state with browser-autofilled inputs.
//
//  THE BUG THIS EXISTS FOR
//  A controlled input takes its value from state, and state only changes when
//  onChange fires. A browser filling saved credentials writes straight to the
//  DOM WITHOUT firing one. So the field visibly holds an email and a password
//  while React still believes both are empty — and a submit button gated on
//  that state stays disabled, with nothing on screen explaining why. The only
//  way out is to type into a field that already looks correct.
//
//  You cannot reproduce this by clicking through the app. It happens only to
//  users who have a saved password, which is most of them. That is exactly why
//  it needs a comment and a test rather than being "obviously fine".
//
//  HOW IT DETECTS THE FILL — three independent nets, because no single one
//  covers every browser:
//    1. A short settle window of timeouts. One frame is too early; Chrome fills
//       shortly AFTER first paint. This is a bounded settle, not a poll.
//    2. `animationstart` from the :-webkit-autofill keyframe declared in
//       Login.css. The keyframe has to animate something REAL — a from/to pair
//       that changes nothing gets optimised away and never fires. outline-color
//       is the inert choice: nothing is drawn, because outline-style is none.
//    3. A capture-phase pointerdown on the document. Every browser sets the
//       pseudo-class; not every browser fires the animation. This reconciles the
//       moment anyone touches the form, so the worst case is one stale frame.
//
//  Usage:
//    const emailRef = useRef(null);
//    const passRef  = useRef(null);
//    useAutofillSync([
//      { ref: emailRef, value: email,    set: setEmail },
//      { ref: passRef,  value: password, set: setPassword },
//    ]);
// ─────────────────────────────────────────────────────────────────────────

/** @param {Array<{ref: object, value: string, set: Function}>} fields */
export default function useAutofillSync(fields) {
    // Read through a ref so the effect mounts exactly once. The array is
    // rebuilt on every render; its CONTENTS matter, its identity does not —
    // depending on it would tear the listeners down and rebuild them each
    // keystroke.
    const latest = useRef(fields);
    latest.current = fields;

    useEffect(() => {
        const sync = () => {
            for (const f of latest.current) {
                const el = f.ref?.current;
                if (el && el.value !== f.value) f.set(el.value);
            }
        };

        const timers = [0, 80, 250, 600].map((ms) => setTimeout(sync, ms));

        const nodes = latest.current.map((f) => f.ref?.current).filter(Boolean);
        nodes.forEach((el) => el.addEventListener("animationstart", sync));
        document.addEventListener("pointerdown", sync, { capture: true });

        return () => {
            timers.forEach(clearTimeout);
            nodes.forEach((el) => el.removeEventListener("animationstart", sync));
            document.removeEventListener("pointerdown", sync, { capture: true });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
